import { useState, useEffect } from 'react';
import useSWR from 'swr';
import { getAvailableStoresV2 } from '@/services/product';
import { calculateDistance } from '@/utils/global';

export function useProductStoreLocator(entity_id, locale) {
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [isFetching, setIsFetching] = useState(true);

  const { data, isLoading: swrLoading } = useSWR(
    entity_id && locale ? `branches/${locale}/${entity_id}` : null,
    () => getAvailableStoresV2(entity_id, locale),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 30 * 60 * 1000,
      focusThrottleInterval: 30 * 60 * 1000,
    }
  );

  useEffect(() => {
    if (!data?.data?.data?.items) return;

    let flattened = (data?.data?.data?.items || []).map((item) => ({
      qty: item?.qty,
      ...item.location,
    }));

    const finalize = (branches) => {
      setSelectedBranches(branches);
      setAllBranches(branches);
      setIsFetching(false);
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLoc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          const withDistance = flattened.map((branch) => ({
            ...branch,
            distance: calculateDistance(
              userLoc.lat,
              userLoc.lng,
              branch.lat,
              branch.lng
            ),
          }));

          const sorted = withDistance.sort((a, b) => a.distance - b.distance);
          finalize(sorted);
        },
        () => finalize(flattened),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    } else {
      finalize(flattened);
    }
  }, [data]);

  // Consider "loading" until SWR and local flattening both finish
  const isLoading = swrLoading || isFetching;

  return {
    selectedBranches,
    setSelectedBranches,
    allBranches,
    isLoading,
  };
}
